import { formatNumber } from "@/v2-common/utils/utils.formatting"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import UsageScoreMeter from "@/v2-backoffice-client/usage/UsageScoreMeter"
import UsageAppMix from "@/v2-backoffice-client/usage/UsageAppMix"
import UsageProductChips from "@/v2-backoffice-client/usage/UsageProductChips"
import UsageChurnRiskBadge from "@/v2-backoffice-client/usage/scope/UsageChurnRiskBadge"
import UsageSeatBar from "@/v2-backoffice-client/usage/scope/UsageSeatBar"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import UsageReportFindings, {
  type UsageReportFinding,
} from "@/v2-backoffice-client/usage/report/UsageReportFindings"
import UsageReportKpi from "@/v2-backoffice-client/usage/report/UsageReportKpi"
import UsageReportProse from "@/v2-backoffice-client/usage/report/UsageReportProse"
import UsageReportSection from "@/v2-backoffice-client/usage/report/UsageReportSection"
import { formatCompactAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import {
  APP_MIX_TOOLTIP,
  SCORE_LEVEL_LABELS,
  getScoreLevel,
} from "@/v2-backoffice-client/usage/usage.constants"
import { useUsageScopeData } from "@/v2-backoffice-client/usage/scope/usage.scope.data"
import { deriveScopeStory } from "@/v2-backoffice-client/usage/scope/usage.scope.story"
import { activeApps } from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import {
  deriveAppInsights,
  deriveUsageInsights,
} from "@/v2-backoffice-client/usage/scope/usage.scope.insights"
import { usageKpiGridClass } from "@/v2-backoffice-client/usage/report/usage.report.utils"

export type UsageScopeOverviewProps = {
  /** Scope to a team's active members. Exactly one scope prop is set. */
  tenantId?: number
  /** Scope to a customer's active users. */
  customerId?: number
  /** The customer's or team's name, for the title and the prose. */
  subject: string
  /**
   * The scope's 12-month billed revenue (the customer panel passes it from
   * its billing query): the money the churn risk puts at stake when the
   * risk is elevated. Teams carry no revenue and pass nothing.
   */
  revenueAtStake?: { amount: number; currency: string | null } | null
}

/** Deep link that pushes a person's panel onto the sheet stack. */
function buildMemberLink(mention: { userId: number }) {
  return `>>?v=user:${mention.userId}&user-view-tabs-tab=usage`
}

// The Overview of a customer's or team's Activity: the key figures across
// every product (seats in use, engagement, activity, churn risk), the story
// in a few plain paragraphs, the plays a renewal or expansion conversation
// starts from, and the product mix when more than Fusion is in play. The
// per-product tabs next to it go deeper. Observations only: CRM workflow
// (contacted, notes, follow-ups) stays in HubSpot.
function UsageScopeOverview(props: UsageScopeOverviewProps) {
  const { tenantId, customerId, subject, revenueAtStake } = props

  const { members, summary, appMix, loading, error } = useUsageScopeData({
    tenantId,
    customerId,
  })

  const story = deriveScopeStory({ summary, appMix, subject, revenueAtStake })
  const plays: UsageReportFinding[] = [
    ...deriveAppInsights(appMix),
    ...deriveUsageInsights(summary),
  ].map((insight) => ({
    key: insight.text,
    text: insight.text,
    tone: insight.tone,
    mentions: insight.mentions,
  }))
  // The mix earns a card only when something besides Fusion v2 is (or was)
  // in play — Console v1 or an MCP server; v2-only scopes have nothing to
  // split.
  const showAppMix = appMix.some((entry) => entry.app !== "v2-console")
  const elevated =
    summary.churnRisk.level === "high" || summary.churnRisk.level === "medium"
  const atStake =
    revenueAtStake != null && revenueAtStake.amount > 0 && elevated
      ? revenueAtStake
      : null

  if (loading && members.length === 0) {
    return <p className="text-sm text-muted-foreground">Loading usage…</p>
  }

  if (summary.seatCount === 0) {
    return (
      <div className="flex flex-col gap-2">
        <ErrorMessage error={error} />
        <p className="usage-report-empty">
          No active seats yet. Usage appears here once people are added and
          start working.
        </p>
      </div>
    )
  }

  return (
    <UsageReportSection
      eyebrow="Overview"
      title={`How ${subject} uses Placepoint`}
      lead={
        tenantId != null
          ? "Seats in use, how much they do and where the risk is, across every product. Fusion detail follows below; the MCP detail is on the customer's Activity tab."
          : "Seats in use, how much they do and where the risk is, across every product. The tabs next to this one go into each product."
      }
      aside={<UsageProductChips apps={activeApps(appMix)} />}
    >
      <ErrorMessage error={error} />

      <div className={usageKpiGridClass(4)}>
        <UsageReportKpi
          label="Seats in use"
          value={
            <>
              {summary.activeUsers28d}
              <span className="text-sm font-normal text-muted-foreground">
                of {summary.seatCount}
              </span>
            </>
          }
          sub={[
            "active in the last 28 days",
            <span key="bar" className="block w-full pt-1">
              <UsageSeatBar bands={summary.seatBands} />
            </span>,
          ]}
        />
        <UsageReportKpi
          label="Engagement"
          value={
            <>
              {summary.avgScore ?? "—"}
              <span className="truncate text-sm font-normal text-muted-foreground">
                {SCORE_LEVEL_LABELS[getScoreLevel(summary.avgScore)]}
              </span>
            </>
          }
          sub={[
            "score of the active seats, out of 100",
            summary.avgScore != null ? (
              <span key="meter" className="block w-full pt-1">
                <UsageScoreMeter score={summary.avgScore} />
              </span>
            ) : (
              "no scored activity yet"
            ),
          ]}
        />
        <UsageReportKpi
          label="Activity"
          value={
            <>
              {formatNumber(
                Math.round(summary.weightedEvents28d ?? summary.events28d),
              )}
              <UsageTrendBadge trendPct={summary.trendPct} />
            </>
          }
          sub={[
            "weighted actions in the last 28 days",
            summary.lastActiveAt ? (
              <>
                last activity <RelativeDate date={summary.lastActiveAt} />
              </>
            ) : (
              "no activity yet"
            ),
          ]}
        />
        <UsageReportKpi
          label="Churn risk"
          tone={elevated ? "warning" : "default"}
          value={
            <UsageChurnRiskBadge
              level={summary.churnRisk.level}
              reasons={summary.churnRisk.reasons}
            />
          }
          sub={
            atStake != null
              ? `${formatCompactAmount(atStake.amount, atStake.currency)} billed in the last 12 months at stake`
              : `${summary.churnRisk.reasons.length} signal${summary.churnRisk.reasons.length === 1 ? "" : "s"} behind the read`
          }
        />
      </div>

      <div className="usage-report-grid usage-report-grid--two">
        <UsageReportProse title="In short" paragraphs={story} />
        {plays.length > 0 && (
          <UsageReportCard
            title="What to do next"
            caption="Derived from the seats, the trend and what has gone untouched. Names open the person's panel."
          >
            <UsageReportFindings
              findings={plays}
              buildMentionTo={buildMemberLink}
            />
          </UsageReportCard>
        )}
        {showAppMix && (
          <UsageReportCard
            title="Which products carry the work"
            caption={APP_MIX_TOOLTIP}
            wide
          >
            <UsageAppMix entries={appMix} />
          </UsageReportCard>
        )}
      </div>
    </UsageReportSection>
  )
}

export default UsageScopeOverview
